<script setup lang="ts">
import { ReviewRequestSchema, UiCard, UiChip, UiText, UiAvatar, UiProse, UiIcon, generateInitials } from '@shopexperts/shared';
import { z } from 'zod';
import { computed } from 'vue';

type ReviewRequest = z.infer<typeof ReviewRequestSchema>;

const props = defineProps<{
  request: ReviewRequest;
}>();

const emit = defineEmits<{
  (e: 'send-reminder', requestId: number): void;
}>();

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const statusConfig = computed(() => {
  switch (props.request.status) {
    case 'pending':
      return { variant: 'warn' as const, text: 'Pending' };
    case 'completed':
      return { variant: 'success' as const, text: 'Review Received' };
    case 'expired':
      return { variant: 'dark' as const, text: 'Expired' };
    case 'cancelled':
      return { variant: 'default' as const, text: 'Cancelled' };
    default:
      return { variant: 'default' as const, text: props.request.status };
  }
});

const isPending = computed(() => props.request.status === 'pending');
const reminders = computed(() => props.request.reminders);
const hasAnyReminderActivity = computed(() => {
  if (!reminders.value) return false;
  return reminders.value.auto_reminder_sent_at || reminders.value.manual_reminder_sent_at || reminders.value.manual_reminder_available;
});

const autoReminderIsPastStep = computed(() => {
  if (!reminders.value) return false;
  return reminders.value.manual_reminder_sent_at || reminders.value.manual_reminder_available;
});

const handleSendReminder = () => {
  emit('send-reminder', props.request.id);
};
</script>

<template>
  <UiCard padding="md">
    <div class="flex items-start justify-between gap-4">
      <div class="flex items-start gap-3 flex-1">
        <UiAvatar
          :initials="generateInitials(props.request.client_name || 'Client')"
          size="xs"
        />
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap mb-0.5">
            <UiText
              as="p"
              variant="h6"
              class="font-semibold"
              :text="props.request.client_name || 'Unknown Client'"
            />
          </div>
          <UiText
            v-if="props.request.client_company"
            as="p"
            variant="p-small"
            class="text-se-gray-600"
            :text="props.request.client_company"
          />
          <UiText
            v-if="props.request.client_email"
            as="p"
            variant="p-small"
            class="text-se-gray-500 mt-0.5"
            :text="props.request.client_email"
          />
        </div>
      </div>

      <div class="flex flex-col items-end gap-1.5 shrink-0">
        <UiChip :variant="statusConfig.variant" size="sm" :text="statusConfig.text" />
        <UiText
          as="span"
          variant="p-small"
          class="text-se-gray-500 whitespace-nowrap"
          :text="`Sent ${formatDate(props.request.sent_at)}`"
        />
      </div>
    </div>

    <div v-if="props.request.project_name" class="mt-3 flex items-center gap-2">
      <UiText as="span" variant="p-small" class="text-se-gray-500" text="Project:" />
      <UiText
        as="span"
        variant="p-small"
        class="font-medium text-se-gray-700"
        :text="props.request.project_name"
      />
    </div>

    <div v-if="props.request.message" class="mt-3 pt-3 border-t border-se-gray-200">
      <UiText as="p" variant="p-small" class="font-medium text-se-gray-700 mb-1.5" text="Your Message:" />
      <UiProse :html="props.request.message" class="text-se-gray-600 text-sm" />
    </div>

    <!-- Reminders timeline -->
    <div
      v-if="isPending && hasAnyReminderActivity"
      class="mt-3 rounded-lg bg-se-gray-100 px-4 py-3"
    >
      <div class="relative flex flex-col gap-3 pl-4">
        <span class="absolute left-[3px] top-[5px] bottom-[5px] w-px bg-se-gray-200" />

        <!-- Auto reminder -->
        <div v-if="reminders?.auto_reminder_sent_at" class="relative flex items-center gap-2">
          <span
            class="absolute -left-4 top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full"
            :class="autoReminderIsPastStep ? 'bg-se-gray-300' : 'bg-green-500'"
          />
          <UiText
            as="span"
            variant="p-small"
            :class="autoReminderIsPastStep ? 'text-se-gray-400' : 'text-se-gray-600'"
            :text="`Auto reminder sent ${formatDate(reminders.auto_reminder_sent_at)}`"
          />
        </div>

        <!-- Manual reminder sent -->
        <div v-if="reminders?.manual_reminder_sent_at" class="relative flex items-center gap-2">
          <span class="absolute -left-4 top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full bg-green-500" />
          <UiText
            as="span"
            variant="p-small"
            class="text-se-gray-600"
            :text="`Manual reminder sent ${formatDate(reminders.manual_reminder_sent_at)}`"
          />
        </div>

        <!-- Manual reminder available -->
        <div v-if="reminders?.manual_reminder_available" class="relative flex items-center gap-2">
          <span class="absolute -left-4 top-1/2 -translate-y-1/2 w-[7px] h-[7px] rounded-full bg-amber-400" />
          <UiText
            as="span"
            variant="p-small"
            class="text-se-gray-600"
            text="You can send a follow-up reminder"
          />
          <button
            class="inline-flex items-center gap-1 text-xs font-semibold text-se-gray-900 bg-white border border-se-gray-200 rounded-md px-2 py-0.5 hover:bg-se-gray-50 transition-colors cursor-pointer shrink-0"
            @click="handleSendReminder"
          >
            <UiIcon name="arrow-send" class="w-3 h-3" />
            <span>Send</span>
          </button>
        </div>
      </div>
    </div>
  </UiCard>
</template>
